import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ContextStatus, SubscriptionStatus } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../auth/AuthenticationContext';
import CompaniesContext from './CompaniesContext';
import updateCompany from '../../../../domain/use_cases/companies/updateCompany';
import deleteCompany from '../../../../domain/use_cases/companies/deleteCompany';
import getCompanySubscriptionsPage from '../../../../domain/use_cases/companies/getCompanySubscriptionsPage';
import UserDoc from '../../../../domain/models/UserDoc';
import { ICompany } from '@leedpulse-monorepo/interfaces';

export const COMPANY_SUBSCRIPTIONS_PAGE_SIZE = 10;
export const COMPANY_USERS_PAGE_SIZE = 10;
const SUBSCRIPTION_SEARCH_DEBOUNCE_MS = 300;
const USER_SEARCH_DEBOUNCE_MS = 300;

export enum SubscriptionType {
  ALL = 'all',
  DEMO = 'demo',
  REGULAR = 'regular',
}

export interface ISubscriptionItem {
  id: string;
  name: string;
  idPlan: string;
  planCredits: number;
  sender: string[];
  status: SubscriptionStatus;
  credits: number;
};

export interface CompanyEditionFormContextProps {
  currentId: string;
  wipCompany: ICompany | null;
  subscriptions: ISubscriptionItem[];
  subscriptionsTotalCount: number;
  subscriptionsTablePage: number;
  subscriptionSearchDraft: string;
  setSubscriptionSearchDraft: (_: string) => void;
  subscriptionTypeFilter: SubscriptionType;
  setSubscriptionTypeFilter: (_: SubscriptionType) => void;
  subscriptionsPageSize: number;
  setSubscriptionsPageSize: (_: number) => void;
  subscriptionsListStatus: ContextStatus;
  goToSubscriptionsPage: (_: number) => Promise<void>;
  users: UserDoc[];
  usersTotalCount: number;
  usersTablePage: number;
  userSearchDraft: string;
  setUserSearchDraft: (_: string) => void;
  usersListStatus: ContextStatus;
  goToUsersPage: (_: number) => void;
  status: ContextStatus;
  initWithCompany: (_: ICompany) => void;
  submitUpdate: () => Promise<void>;
  submitDelete: () => Promise<void>;
  onIdStripeChange: (_: string) => void;
  onNotesChange: (_: string) => void;
  onNameChange: (_: string) => void;
  onCreatedChange: (_: Date) => void;
  onUpdatedChange: (_: Date) => void;
}

const CompanyEditionFormContext = createContext<CompanyEditionFormContextProps>({
  currentId: '',
  wipCompany: null as ICompany | null,
  subscriptions: [] as ISubscriptionItem[],
  subscriptionsTotalCount: 0,
  subscriptionsTablePage: 1,
  subscriptionSearchDraft: '',
  setSubscriptionSearchDraft: (_: string) => {
    return;
  },
  subscriptionTypeFilter: SubscriptionType.ALL,
  setSubscriptionTypeFilter: (_: SubscriptionType) => {
    return;
  },
  subscriptionsPageSize: COMPANY_SUBSCRIPTIONS_PAGE_SIZE,
  setSubscriptionsPageSize: (_: number) => {
    return;
  },
  subscriptionsListStatus: ContextStatus.IDLE,
  goToSubscriptionsPage: async (_: number) => Promise.resolve(),
  users: [] as UserDoc[],
  usersTotalCount: 0,
  usersTablePage: 1,
  userSearchDraft: '',
  setUserSearchDraft: (_: string) => {
    return;
  },
  usersListStatus: ContextStatus.IDLE,
  goToUsersPage: (_: number) => {
    return;
  },
  status: ContextStatus.IDLE,
  initWithCompany: (_: ICompany) => {
    return;
  },
  submitUpdate: async () => Promise.resolve(),
  submitDelete: async () => Promise.resolve(),
  onIdStripeChange: (_: string) => {
    return;
  },
  onNotesChange: (_: string) => {
    return;
  },
  onNameChange: (_: string) => {
    return;
  },
  onCreatedChange: (_: Date) => {
    return;
  },
  onUpdatedChange: (_: Date) => {
    return;
  },
});

export const CompanyEditionFormProvider: React.FC<React.PropsWithChildren<{ company?: ICompany | null }>> = ({
  children,
  company,
}) => {
  const [currentId, setCurrentId] = useState('');
  const [wipCompany, setWipCompany] = useState<ICompany | null>(null);

  const [subscriptions, setSubscriptions] = useState<ISubscriptionItem[]>(
    []
  );
  const [subscriptionsTotalCount, setSubscriptionsTotalCount] = useState(0);
  const [subscriptionsTablePage, setSubscriptionsTablePage] = useState(1);
  const [subscriptionSearchDraft, setSubscriptionSearchDraft] = useState('');
  const [debouncedSubscriptionSearch, setDebouncedSubscriptionSearch] =
    useState('');
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] =
    useState<SubscriptionType>(SubscriptionType.ALL);
  const [subscriptionsPageSize, setSubscriptionsPageSize] = useState(
    COMPANY_SUBSCRIPTIONS_PAGE_SIZE
  );
  const [subscriptionsListStatus, setSubscriptionsListStatus] = useState(
    ContextStatus.IDLE
  );

  const [status, setStatus] = useState(ContextStatus.IDLE);

  const [users, setUsers] = useState<UserDoc[]>([]);
  const [usersTotalCount, setUsersTotalCount] = useState(0);
  const [usersTablePage, setUsersTablePage] = useState(1);
  const [userSearchDraft, setUserSearchDraft] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [usersListStatus, setUsersListStatus] = useState(ContextStatus.IDLE);

  const authContext = useContext(AuthenticationContext);
  const companiesContext = useContext(CompaniesContext);

  const subscriptionsFetchGenRef = useRef(0);
  const wipCompanyRef = useRef<ICompany | null>(null);

  // Muter une ref pendant le rendu n'est pas sûr en rendu concurrent (le rendu
  // peut être abandonné). Les lecteurs sont tous des callbacks / effets
  // déclarés plus bas, donc cet effet s'exécute avant eux.
  useEffect(() => {
    wipCompanyRef.current = wipCompany;
  }, [wipCompany]);

  const fetchSubscriptionsPage = useCallback(
    async (
      page: number,
      companyId: string,
      searchQuery: string,
      typeFilter: SubscriptionType,
      pageSize: number
    ) => {
      const gen = ++subscriptionsFetchGenRef.current;
      setSubscriptionsListStatus(ContextStatus.LOADING);
      try {
        const { subscriptions: items, totalCount } =
          await getCompanySubscriptionsPage(companyId, {
            page,
            pageSize,
            search: searchQuery,
            type: typeFilter,
          });
        if (gen !== subscriptionsFetchGenRef.current) return;
        setSubscriptionsTotalCount(totalCount);
        setSubscriptions(items);
        setSubscriptionsTablePage(page);
        setSubscriptionsListStatus(ContextStatus.SUCCESS);
      } catch (error) {
        if (gen !== subscriptionsFetchGenRef.current) return;
        console.error('Erreur lors de la récupération des abonnements :', error);
        setSubscriptionsListStatus(ContextStatus.ERROR);
      }
    },
    []
  );

  const companySubscriptionIdsKey =
    wipCompany == null
      ? ''
      : Object.keys(wipCompany.id_subscriptions || {}).sort().join(',');

  const goToSubscriptionsPage = useCallback(
    async (page: number) => {
      const wc = wipCompanyRef.current;
      if (wc == null) return;
      if (Object.keys(wc.id_subscriptions || {}).length === 0) return;
      await fetchSubscriptionsPage(
        page,
        wc.id,
        debouncedSubscriptionSearch,
        subscriptionTypeFilter,
        subscriptionsPageSize
      );
    },
    [
      debouncedSubscriptionSearch,
      subscriptionTypeFilter,
      subscriptionsPageSize,
      fetchSubscriptionsPage,
    ]
  );

  const initWithCompany = (company: ICompany) => {
    subscriptionsFetchGenRef.current += 1;
    setSubscriptionSearchDraft('');
    setDebouncedSubscriptionSearch('');
    setSubscriptionTypeFilter(SubscriptionType.ALL);
    setSubscriptionsPageSize(COMPANY_SUBSCRIPTIONS_PAGE_SIZE);
    setUserSearchDraft('');
    setDebouncedUserSearch('');
    setUsers([]);
    setUsersTotalCount(0);
    setUsersTablePage(1);
    setUsersListStatus(ContextStatus.IDLE);
    setCurrentId(company.id);
    setWipCompany(company);
  };

  // Every tab reads this context and only the open one is mounted: it starts from the page, so a
  // reload on another tab than the general information loads its data too
  useEffect(() => {
    if (company && company.id !== currentId) initWithCompany(company);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  const submitUpdate = async () => {
    if (wipCompany === null || authContext.authentication === null) return;
    setStatus(ContextStatus.LOADING);
    try {
      await updateCompany(currentId, wipCompany, authContext.authentication);
      setStatus(ContextStatus.SUCCESS);
    } catch {
      setStatus(ContextStatus.ERROR);
    }
  };

  const submitDelete = async () => {
    if (wipCompany === null || authContext.authentication === null) return;
    setStatus(ContextStatus.LOADING);
    try {
      await deleteCompany(wipCompany.id, authContext.authentication);
      setStatus(ContextStatus.SUCCESS);
    } catch {
      setStatus(ContextStatus.ERROR);
    }
  };

  const onIdStripeChange = (idStripe: string) => {
    if (wipCompany === null) return;
    setWipCompany((o) => (o ? { ...o, id_stripe: idStripe } : null));
  };

  const onNotesChange = (notes: string) => {
    if (wipCompany === null) return;
    setWipCompany((o) => (o ? { ...o, notes } : null));
  };

  const onNameChange = (name: string) => {
    if (wipCompany === null) return;
    setWipCompany((o) => (o ? { ...o, name } : null));
  };

  const onCreatedChange = (created: Date) => {
    if (wipCompany === null) return;
    setWipCompany((o) => (o ? { ...o, created: created.getTime() } : null));
  };

  const onUpdatedChange = (updated: Date) => {
    if (wipCompany === null) return;
    setWipCompany((o) => (o ? { ...o, updated: updated.getTime() } : null));
  };

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSubscriptionSearch(subscriptionSearchDraft.trim());
    }, SUBSCRIPTION_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [subscriptionSearchDraft]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedUserSearch(userSearchDraft.trim());
    }, USER_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [userSearchDraft]);

  // Reset to first page whenever the user search changes.
  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  const [lastUserSearch, setLastUserSearch] = useState(debouncedUserSearch);
  if (debouncedUserSearch !== lastUserSearch) {
    setLastUserSearch(debouncedUserSearch);
    setUsersTablePage(1);
  }

  // Derive paginated users from the full list fetched by CompaniesContext.
  useEffect(() => {
    if (wipCompany == null) return;

    const allUsers = companiesContext.companyUsers;
    const q = debouncedUserSearch.trim().toLowerCase();
    const filtered = q
      ? allUsers.filter((u) => {
          const fullName = `${u.firstname || ''} ${u.lastname || ''}`.toLowerCase();
          const email = (u.email || '').toLowerCase();
          const emailAuth = (u.emailAuthentication || '').toLowerCase();
          return (
            fullName.includes(q) ||
            email.includes(q) ||
            emailAuth.includes(q)
          );
        })
      : allUsers;

    // Quatre etats couples dans un seul effet : le total, la page bornee, la
    // tranche affichee et le statut. La page se borne a partir d'elle-meme
    // (setUsersTablePage(page)), donc le calcul n'est pas une fonction pure de
    // ses entrees.
    // eslint-disable-next-line react/set-state-in-effect
    setUsersTotalCount(filtered.length);

    const maxPage = Math.max(1, Math.ceil(filtered.length / COMPANY_USERS_PAGE_SIZE));
    const page = Math.min(usersTablePage, maxPage);
    if (page !== usersTablePage) setUsersTablePage(page);

    const start = (page - 1) * COMPANY_USERS_PAGE_SIZE;
    setUsers(filtered.slice(start, start + COMPANY_USERS_PAGE_SIZE));

    setUsersListStatus(
      companiesContext.status === ContextStatus.LOADING
        ? ContextStatus.LOADING
        : ContextStatus.SUCCESS
    );
  }, [
    companiesContext.companyUsers,
    companiesContext.status,
    debouncedUserSearch,
    usersTablePage,
    wipCompany?.id,
  ]);

  const goToUsersPage = useCallback((page: number) => {
    setUsersTablePage(page);
  }, []);

  useEffect(() => {
    const wc = wipCompanyRef.current;
    if (wc == null) return;
    if (Object.keys(wc.id_subscriptions || {}).length === 0) {
      setSubscriptions([]);
      setSubscriptionsTotalCount(0);
      setSubscriptionsTablePage(1);
      setSubscriptionsListStatus(ContextStatus.SUCCESS);
      return;
    }
    void fetchSubscriptionsPage(
      1,
      wc.id,
      debouncedSubscriptionSearch,
      subscriptionTypeFilter,
      subscriptionsPageSize
    );
  }, [
    wipCompany?.id,
    companySubscriptionIdsKey,
    debouncedSubscriptionSearch,
    subscriptionTypeFilter,
    subscriptionsPageSize,
    fetchSubscriptionsPage,
  ]);

  return (
    <CompanyEditionFormContext.Provider
      value={{
        currentId,
        wipCompany,
        subscriptions,
        subscriptionsTotalCount,
        subscriptionsTablePage,
        subscriptionSearchDraft,
        setSubscriptionSearchDraft,
        subscriptionTypeFilter,
        setSubscriptionTypeFilter,
        subscriptionsPageSize,
        setSubscriptionsPageSize,
        subscriptionsListStatus,
        goToSubscriptionsPage,
        users,
        usersTotalCount,
        usersTablePage,
        userSearchDraft,
        setUserSearchDraft,
        usersListStatus,
        goToUsersPage,
        status,
        initWithCompany,
        submitUpdate,
        submitDelete,
        onIdStripeChange,
        onNotesChange,
        onNameChange,
        onCreatedChange,
        onUpdatedChange,
      }}
    >
      {children}
    </CompanyEditionFormContext.Provider>
  );
};

export default CompanyEditionFormContext;
